<template>
  <div>
    <!-- Categories settings sidebar -->
    <b-sidebar
      id="book-list-categories-sidebar"
      v-model="showCategoriesSidebar"
      bg-variant="white"
      sidebar-class="sidebar-lg"
      right
      no-header
      backdrop
      shadow
    >
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">{{ $t(categoryPluralLabel) }}</h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="showCategoriesSidebar = false"
        />
      </div>

      <div class="p-2">
        <b-form
          v-if="rights.add || rights.edit"
          @submit.prevent="saveCategory"
        >
          <b-form-group :label="$t(categoryNameLabel)">
            <b-form-input
              v-model.trim="categoryForm.name"
              :placeholder="$t(categoryPlaceholder)"
              required
            />
          </b-form-group>
          <div class="d-flex mb-2">
            <b-button
              v-if="categoryForm.id > 0 ? rights.edit : rights.add"
              variant="primary"
              type="submit"
              class="flex-grow-1"
              :disabled="categorySaving"
            >
              <b-spinner v-if="categorySaving" small />
              <span v-else>{{
                categoryForm.id > 0 ? $t("Update") : $t("Add New")
              }}</span>
            </b-button>
            <b-button
              v-if="categoryForm.id > 0 && rights.edit"
              variant="outline-secondary"
              class="ml-1"
              @click="resetCategoryForm"
            >
              {{ $t("Cancel") }}
            </b-button>
          </div>
        </b-form>

        <hr v-if="rights.add || rights.edit" />

        <div v-if="categoriesLoading" class="text-center my-2">
          <b-spinner variant="primary" />
        </div>
        <div v-else-if="!categories.length" class="text-center text-muted py-2">
          {{ $t("No records found") }}
        </div>
        <div v-else>
          <div
            v-for="cat in categories"
            :key="cat.id"
            class="d-flex align-items-center justify-content-between py-1 border-bottom"
          >
            <div class="d-flex align-items-center flex-grow-1 mr-1">
              <b-form-checkbox
                v-if="rights.edit"
                switch
                :checked="cat.isActive !== false"
                :disabled="categoryStatusUpdatingId === cat.id"
                class="mr-1 mb-0"
                @change="toggleCategoryStatus(cat, $event)"
              />
              <span :class="{ 'text-muted': cat.isActive === false }">
                {{ cat.name }}
              </span>
            </div>
            <div v-if="rights.edit || rights.delete" class="d-flex flex-shrink-0">
              <b-button
                v-if="rights.edit"
                variant="flat-primary"
                class="btn-icon p-0 mr-50"
                @click="editCategory(cat)"
              >
                <feather-icon icon="EditIcon" size="14" />
              </b-button>
              <b-button
                v-if="rights.delete"
                variant="flat-danger"
                class="btn-icon p-0"
                @click="deleteCategory(cat)"
              >
                <feather-icon icon="Trash2Icon" size="14" />
              </b-button>
            </div>
          </div>

          <b-pagination
            v-if="categoryTotalCount > categoryPerPage"
            v-model="categoryCurrentPage"
            :total-rows="categoryTotalCount"
            :per-page="categoryPerPage"
            class="mt-2"
            align="center"
            size="sm"
            @input="loadCategories"
          />
        </div>
      </div>
    </b-sidebar>

    <b-card class="planner-list-card">
      <div class="gradient-line-wrapper"></div>

      <div class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2">
        <div>
          <h5 class="mb-0 resource-title">{{ $t("Book List") }}</h5>
          <small class="text-muted">{{
            $t("Manage book lists by class and section")
          }}</small>
        </div>
      </div>

      <div class="pagination-controls-top d-flex justify-content-between align-items-center mb-2">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <div class="page-size-wrapper">
            <label class="page-size-label">{{ $t("Show") }}:</label>
            <v-select
              v-model="perPage"
              :options="pageSizeOptions"
              :reduce="(option) => option.value"
              label="label"
              :clearable="false"
              :searchable="false"
              append-to-body
              class="page-size-select-compact"
              @input="onPageSizeChange"
            />
          </div>
        </div>

        <div class="pagination-count-display">
          <span class="count-text">
            {{ $t("Showing") }} <strong>{{ displayedRange.start }}</strong>
            {{ $t("to") }} <strong>{{ displayedRange.end }}</strong>
            {{ $t("of") }} <strong>{{ totalCount }}</strong>
          </span>
        </div>

        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact mb-0"
            @input="loadBookLists"
          />
        </div>
      </div>

      <b-row class="align-items-center filter-row mb-2">
        <b-col cols="12" class="filter-toolbar d-flex align-items-center">
          <div class="filter-fields-group filter-fields-group--cols-3 d-flex align-items-center">
            <v-select
              v-model="filters.classID"
              :options="classes"
              :clearable="true"
              label="name"
              :reduce="(val) => val.id"
              :placeholder="$t('Select class')"
              append-to-body
              class="filter-control filter-field-item"
              @input="onFilterClassChange"
            />
            <v-select
              v-model="filters.sectionID"
              :options="filterSections"
              :clearable="true"
              label="section"
              :reduce="(val) => val.id"
              :placeholder="$t('Select section')"
              :disabled="!filters.classID"
              append-to-body
              class="filter-control filter-field-item"
              @input="onFilterChange"
            />
          </div>

          <div class="filter-actions-group d-flex align-items-center ml-auto">
            <b-button
              variant="outline-secondary"
              class="action-btn-filter text-nowrap mr-50"
              v-b-tooltip.hover.top
              :title="$t(categoryPluralLabel)"
              @click="openCategoriesSidebar"
            >
              <feather-icon icon="SettingsIcon" size="16" />
            </b-button>
            <b-button
              v-if="rights.add"
              variant="gradient-primary"
              class="action-btn-filter add-sheet-btn text-nowrap"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              @click="goToAddForm"
            >
              <feather-icon icon="PlusIcon" class="mr-50" />
              <span>{{ $t("Add Book List") }}</span>
            </b-button>
          </div>
        </b-col>
      </b-row>

      <div class="table-scroll-container">
        <b-table
          class="planner-list-table mt-1"
          :items="bookLists"
          :fields="fields"
          :busy="dataLoading"
          show-empty
          :responsive="false"
          hover
          small
        >
          <template #empty>
            <h3 v-if="!dataLoading" class="mt-1 mb-1 text-center">
              {{ $t("No records found") }}
            </h3>
          </template>

          <template #table-busy>
            <tbody>
              <tr v-for="n in 5" :key="'skeleton-' + n" class="skeleton-row">
                <td
                  v-for="(field, index) in fields"
                  :key="'skeleton-cell-' + index"
                  class="skeleton-cell"
                >
                  <div class="skeleton-loader"></div>
                </td>
              </tr>
            </tbody>
          </template>

          <template #cell(index)="data">
            {{ (currentPage - 1) * perPage + data.index + 1 }}
          </template>

          <template #cell(className)="data">
            <span class="bl-table-class-badge">{{ data.value || "—" }}</span>
          </template>

          <template #cell(sectionName)="data">
            <span class="bl-table-section-badge">
              <feather-icon icon="LayersIcon" size="12" class="bl-table-section-badge__icon" />
              <span>{{ data.value || "—" }}</span>
            </span>
          </template>

          <template #cell(itemCount)="data">
            <span class="bl-table-count-badge">{{ data.value }}</span>
          </template>

          <template #head(actions)="data">
            <div class="text-center">
              <span>{{ data.label }}</span>
            </div>
          </template>

          <template #cell(actions)="{ item }">
            <div class="d-flex justify-content-center">
              <b-button
                variant="outline-primary"
                v-b-tooltip.hover.top
                :title="$t('View')"
                class="action-btn-outline mr-1"
                @click="goToViewForm(item)"
              >
                <feather-icon icon="EyeIcon" />
              </b-button>
              <b-button
                v-if="usePublisherLabel"
                variant="gradient-primary"
                v-b-tooltip.hover.top
                :title="$t('Print')"
                class="action-btn-table mr-1"
                @click="printCrestViewBookList(item)"
              >
                <feather-icon icon="PrinterIcon" size="16" />
              </b-button>
              <b-button
                v-if="rights.edit"
                variant="gradient-primary"
                v-b-tooltip.hover.top
                :title="$t('Edit')"
                class="action-btn-table mr-1"
                @click="goToEditForm(item)"
              >
                <feather-icon icon="EditIcon" />
              </b-button>
              <b-button
                v-if="rights.delete"
                variant="outline-danger"
                v-b-tooltip.hover.top
                :title="$t('Delete')"
                class="action-btn-outline delete-btn"
                @click="deleteBookList(item)"
              >
                <feather-icon icon="Trash2Icon" />
              </b-button>
            </div>
          </template>
        </b-table>
      </div>
    </b-card>

    <!-- Categories sidebar and list content -->
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BTable,
  BPagination,
  BForm,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
  BSpinner,
  BSidebar,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapActions } from "vuex";
import Swal from "sweetalert2";
import Ripple from "vue-ripple-directive";
import {
  HEAD_OFFICE_CAMPUS_ID,
} from "./bookListUtils";

export default {
  name: "BookList",
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    BPagination,
    BForm,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BSpinner,
    BSidebar,
    vSelect,
  },
  directives: {
    "b-tooltip": VBTooltip,
    Ripple,
  },
  data() {
    return {
      rights: {},
      bookLists: [],
      classes: [],
      classesData: [],
      filterSections: [],
      dataLoading: false,
      currentPage: 1,
      perPage: 20,
      totalCount: 0,
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
      ],
      filters: {
        classID: null,
        sectionID: null,
      },
      fields: [
        {
          label: "S.No",
          key: "index",
          thClass: "text-center",
          tdClass: "text-center",
        },
        { key: "className", label: "Class", thClass: "text-center", tdClass: "text-center" },
        {
          key: "sectionName",
          label: "Section",
          thClass: "text-center",
          tdClass: "text-center",
        },
        { key: "itemCount", label: "Items", thClass: "text-center", tdClass: "text-center" },
        {
          key: "actions",
          label: "Actions",
          thClass: "text-center",
          tdClass: "text-center",
        },
      ],
      showCategoriesSidebar: false,
      categories: [],
      categoriesLoading: false,
      categorySaving: false,
      categoryStatusUpdatingId: null,
      categoryCurrentPage: 1,
      categoryPerPage: 10,
      categoryTotalCount: 0,
      categoryForm: {
        id: 0,
        name: "",
      },
    };
  },
  computed: {
    reportDomain() {
      return String(this.$store.state.report || "")
        .replace(/_/g, "")
        .trim()
        .toLowerCase();
    },
    usePublisherLabel() {
      return [
        "myskoolcrestview",
        "myskoolcrestviewacademy",
      ].includes(this.reportDomain);
    },
    categoryPluralLabel() {
      return this.usePublisherLabel ? "Publishers" : "Categories";
    },
    categoryNameLabel() {
      return this.usePublisherLabel ? "Publisher name" : "Category name";
    },
    categoryPlaceholder() {
      return this.usePublisherLabel
        ? "Enter publisher name"
        : "Enter category name";
    },
    headOfficeCampusID() {
      return HEAD_OFFICE_CAMPUS_ID;
    },
    userID() {
      return (
        this.$store.state.userData.userID ||
        this.$store.state.userData.localID ||
        this.$store.state.userData.userLinkID ||
        0
      );
    },
    displayedRange() {
      if (this.totalCount === 0) return { start: 0, end: 0 };
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.totalCount);
      return { start, end };
    },
  },
  watch: {
    $route(to, from) {
      if (
        to.name === "book-list" &&
        (from.name === "book-list-add" ||
          from.name === "book-list-edit" ||
          from.name === "book-list-view" ||
          from.name === "book-list-crestview-add" ||
          from.name === "book-list-crestview-edit" ||
          from.name === "book-list-crestview-view")
      ) {
        this.loadBookLists();
      }
    },
  },
  created() {
    let right = {};
    this.$store.state.menu.forEach((el) => {
      el.children.forEach((ch) => {
        right[ch.route] = {
          view: ch.view,
          add: ch.add,
          edit: ch.edit,
          delete: ch.delete,
        };
      });
    });
    this.$store.commit("setRights", right);

    if (!this.$store.state.rights[this.$route.name]) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }
    this.rights = this.$store.state.rights[this.$route.name];
    if (!this.rights.view) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }

    this.loadClasses();
    this.loadBookLists();
  },
  methods: {
    ...mapActions({ get: "get", post: "post", delete: "delete" }),

    apiBase() {
      return this.$store.state.domain;
    },

    dbParam() {
      return encodeURIComponent(this.$store.state.userData.db);
    },

    normalizeListResponse(response) {
      if (response && Array.isArray(response.data)) {
        return {
          data: response.data,
          total: Number(response.total ?? response.totalCount ?? response.data.length),
        };
      }
      if (Array.isArray(response)) {
        return { data: response, total: response.length };
      }
      return { data: [], total: 0 };
    },

    mapBookListRow(item) {
      const items = item.items || item.bookListItems || [];
      return {
        ...item,
        className:
          item.className ||
          item.meta_class ||
          item.clsName ||
          this.getClassName(item.classID),
        sectionName:
          item.sectionName ||
          item.meta_section ||
          item.secName ||
          this.getSectionName(item.classID, item.sectionID),
        itemCount:
          item.itemCount ??
          item.itemsCount ??
          (Array.isArray(items) ? items.length : 0),
      };
    },

    getClassName(classID) {
      const cls = this.classes.find((c) => Number(c.id) === Number(classID));
      return cls ? cls.name : "—";
    },

    getSectionName(classID, sectionID) {
      if (Number(sectionID) === 0) {
        return this.$t("All Sections");
      }
      const classData = this.classesData.find(
        (c) => Number(c.cls?.id) === Number(classID)
      );
      const sec = (classData?.sections || []).find(
        (s) => Number(s.id) === Number(sectionID)
      );
      return sec ? sec.section || sec.name : "—";
    },

    buildSectionOptions(classId) {
      const selected = this.classesData.find(
        (c) => Number(c.cls?.id) === Number(classId)
      );
      const sections = (selected?.sections || [])
        .filter((s) => s && s.id)
        .map((s) => ({
          id: s.id,
          section: s.section || s.name || `Section ${s.id}`,
        }));
      return [{ id: 0, section: this.$t("All Sections") }, ...sections];
    },

    async loadClasses() {
      try {
        const obj = {
          url:
            this.apiBase() +
            "myclasses/LoadClasses?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.headOfficeCampusID +
            "&dID=0",
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        const data = response?.data !== undefined ? response.data : response;
        this.classesData = Array.isArray(data) ? data : [];
        this.classes = this.classesData
          .map((el) => el.cls)
          .filter((cls) => cls && cls.id);
      } catch (error) {
        console.error("Error loading classes:", error);
        this.classes = [];
        this.classesData = [];
      }
    },

    loadSectionsForClass(classId) {
      this.filterSections = classId ? this.buildSectionOptions(classId) : [];
    },

    onFilterClassChange() {
      this.filters.sectionID = null;
      this.loadSectionsForClass(this.filters.classID);
      this.onFilterChange();
    },

    onFilterChange() {
      this.currentPage = 1;
      this.loadBookLists();
    },

    onPageSizeChange() {
      this.currentPage = 1;
      this.loadBookLists();
    },

    async loadBookLists(page) {
      if (page) this.currentPage = page;
      this.dataLoading = true;
      try {
        let url =
          this.apiBase() +
          "BookList/GetByPagination?db=" +
          this.dbParam() +
          "&campusID=" +
          this.headOfficeCampusID +
          "&pageNumber=" +
          this.currentPage +
          "&pageSize=" +
          this.perPage;
        if (this.filters.classID) url += "&classID=" + this.filters.classID;
        if (this.filters.classID && this.filters.sectionID != null) {
          url += "&sectionID=" + this.filters.sectionID;
        }

        const response = await this.get({
          url,
          token: this.$store.state.userData.token,
        });
        const { data, total } = this.normalizeListResponse(response);
        this.bookLists = data.map((row) => this.mapBookListRow(row));
        this.totalCount = total;
      } catch (error) {
        console.error("Error loading book lists:", error);
        this.bookLists = [];
        this.totalCount = 0;
      } finally {
        this.dataLoading = false;
      }
    },

    openCategoriesSidebar() {
      this.showCategoriesSidebar = true;
      this.resetCategoryForm();
      this.loadCategories();
    },

    resetCategoryForm() {
      this.categoryForm = { id: 0, name: "" };
    },

    editCategory(cat) {
      if (!this.rights.edit) return;
      this.categoryForm = { id: cat.id, name: cat.name || "" };
    },

    async loadCategories() {
      this.categoriesLoading = true;
      try {
        const url =
          this.apiBase() +
          "BookListCategories/GetByPagination?db=" +
          this.dbParam() +
          "&campusID=" +
          this.headOfficeCampusID +
          "&pageNumber=" +
          this.categoryCurrentPage +
          "&pageSize=" +
          this.categoryPerPage;
        const response = await this.get({
          url,
          token: this.$store.state.userData.token,
        });
        const { data, total } = this.normalizeListResponse(response);
        this.categories = data;
        this.categoryTotalCount = total;
      } catch (error) {
        console.error("Error loading categories:", error);
        this.categories = [];
        this.categoryTotalCount = 0;
      } finally {
        this.categoriesLoading = false;
      }
    },

    async saveCategory() {
      if (!this.categoryForm.name) return;
      if (this.categoryForm.id > 0 ? !this.rights.edit : !this.rights.add) return;
      this.categorySaving = true;
      try {
        const url =
          this.apiBase() +
          "BookListCategories/Save?db=" +
          this.dbParam() +
          "&campusID=" +
          this.headOfficeCampusID +
          "&userID=" +
          this.userID;
        const response = await this.post({
          url,
          body: {
            id: this.categoryForm.id || 0,
            name: this.categoryForm.name,
          },
          token: this.$store.state.userData.token,
          context: this,
        });
        if (response !== false && response !== null) {
          this.resetCategoryForm();
          await this.loadCategories();
        }
      } catch (error) {
        console.error("Error saving category:", error);
      } finally {
        this.categorySaving = false;
      }
    },

    async deleteCategory(cat) {
      if (!this.rights.delete) return;
      const result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("Yes, delete it!"),
        cancelButtonText: this.$t("Cancel"),
      });
      if (!result.isConfirmed) return;

      try {
        const url =
          this.apiBase() +
          "BookListCategories/Delete?db=" +
          this.dbParam() +
          "&campusID=" +
          this.headOfficeCampusID +
          "&id=" +
          cat.id +
          "&userID=" +
          this.userID;
        const success = await this.delete({
          url,
          token: this.$store.state.userData.token,
          context: this,
        });
        if (success) {
          await this.loadCategories();
        }
      } catch (error) {
        console.error("Error deleting category:", error);
      }
    },

    async toggleCategoryStatus(cat, checked) {
      if (!this.rights.edit) return;
      this.categoryStatusUpdatingId = cat.id;
      try {
        const url =
          this.apiBase() +
          "BookListCategories/SetStatus?db=" +
          this.dbParam() +
          "&campusID=" +
          this.headOfficeCampusID +
          "&id=" +
          cat.id +
          "&isActive=" +
          checked +
          "&userID=" +
          this.userID;
        await this.post({
          url,
          body: {},
          token: this.$store.state.userData.token,
          context: this,
        });
        cat.isActive = checked;
      } catch (error) {
        console.error("Error updating category status:", error);
      } finally {
        this.categoryStatusUpdatingId = null;
      }
    },

    goToAddForm() {
      if (!this.rights.add) return;
      this.$router.push({
        name: this.usePublisherLabel
          ? "book-list-crestview-add"
          : "book-list-add",
      });
    },

    goToEditForm(item) {
      if (!this.rights.edit) return;
      this.$router.push({
        name: this.usePublisherLabel
          ? "book-list-crestview-edit"
          : "book-list-edit",
        params: { id: item.id },
      });
    },

    goToViewForm(item) {
      if (!this.rights.view) return;
      this.$router.push({
        name: this.usePublisherLabel
          ? "book-list-crestview-view"
          : "book-list-view",
        params: { id: item.id },
      });
    },

    printCrestViewBookList(item) {
      if (!this.rights.view || !item || !item.id) return;
      const route = this.$router.resolve({
        name: "book-list-crestview-print",
        query: {
          id: item.id,
          db: this.$store.state.userData.db,
          campusID: this.headOfficeCampusID,
        },
      });
      window.open(route.href, "_blank");
    },

    async deleteBookList(item) {
      if (!this.rights.delete) return;
      const result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("Yes, delete it!"),
        cancelButtonText: this.$t("Cancel"),
        customClass: {
          confirmButton: "btn btn-danger",
          cancelButton: "btn btn-outline-secondary ml-1",
        },
        buttonsStyling: false,
      });
      if (!result.isConfirmed) return;

      try {
        const url =
          this.apiBase() +
          "BookList/Delete?db=" +
          this.dbParam() +
          "&campusID=" +
          this.headOfficeCampusID +
          "&id=" +
          item.id +
          "&userID=" +
          this.userID;
        const success = await this.delete({
          url,
          token: this.$store.state.userData.token,
          context: this,
        });
        if (success) {
          await this.loadBookLists();
        }
      } catch (error) {
        console.error("Error deleting book list:", error);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../planner/plannerListTable.scss";

.bl-table-class-badge,
.bl-table-section-badge,
.bl-table-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
}

.bl-table-class-badge {
  padding: 0.38rem 0.8rem;
  border-radius: 999px;
  background: linear-gradient(180deg, #f8f7ff 0%, #f3f0ff 100%);
  color: #7367f0;
  border: 1px solid #e0d8ff;
  box-shadow: 0 1px 2px rgba(115, 103, 240, 0.06);
}

.bl-table-section-badge {
  gap: 0.35rem;
  padding: 0.38rem 0.8rem;
  border-radius: 10px;
  background: #fff;
  color: #475569;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);

  &__icon {
    color: #94a3b8;
    flex-shrink: 0;
  }
}

.bl-table-count-badge {
  min-width: 30px;
  height: 30px;
  padding: 0 0.5rem;
  border-radius: 999px;
  background: #f8fafc;
  color: #64748b;
  border: 1px solid #e2e8f0;
  font-weight: 700;
  font-size: 0.8rem;
}

::v-deep .bl-edit-modal-content {
  border: none;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(45, 27, 105, 0.18);
}

::v-deep .bl-edit-modal-body,
::v-deep .bl-view-modal-body {
  padding: 0;
}

.bl-edit-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.35rem 1.5rem 1.15rem;
  background: #fff;
  color: #2d1b69;
  border-bottom: 1px solid #f0edff;
}

.bl-edit-modal-title-wrap {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  min-width: 0;
}

.bl-edit-modal-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(124, 93, 250, 0.12);
  color: #7c5dfa;
  flex-shrink: 0;
}

.bl-edit-modal-title {
  font-size: 1.15rem;
  font-weight: 800;
  color: #2d1b69;
}

.bl-edit-modal-sub {
  margin-top: 0.2rem;
  font-size: 0.78rem;
  color: #8a86b7;
}

.bl-edit-modal-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 10px;
  background: #f8f7ff;
  color: #6e6b7b;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #f0edff;
    color: #2d1b69;
  }
}

.bl-edit-body {
  padding: 1.25rem 1.5rem 1.5rem;
  background: #fff;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
}

.bl-view-body {
  padding-bottom: 0.5rem;
}

.bl-edit-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 3rem 1.5rem;
  color: #8a86b7;
}

.bl-form-section {
  margin-bottom: 1rem !important;
}

.bl-class-section {
  position: relative;
  z-index: 20;
  overflow: visible !important;
}

.bl-form-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 2rem 1rem;
  color: #8a86b7;
  font-size: 0.85rem;
}

.bl-form-hint {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #faf9ff;
  border: 1px dashed #e0d8ff;
  border-radius: 10px;
  color: #8a86b7;
  font-size: 0.82rem;
}

.bl-edit-category-card {
  background: #fff;
  border: 1px solid #e8e4ff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(124, 93, 250, 0.08);
  position: relative;
  z-index: 1;
}

.bl-edit-category-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.9rem 1.1rem;
  background: linear-gradient(90deg, #f8f7ff 0%, #fff 100%);
  border-bottom: 1px solid #f0edff;
}

.bl-edit-category-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;

  strong {
    font-size: 0.95rem;
    color: #2d1b69;
  }
}

.bl-edit-category-meta {
  font-size: 0.72rem;
  color: #8a86b7;
  margin-top: 0.15rem;
}

.record-qty-badge {
  min-width: 32px;
  font-weight: 700;
  font-size: 0.78rem;
}

.record-empty-state {
  text-align: center;
  padding: 2rem 1rem;
  color: #8a86b7;
  background: #faf9ff;
  border-top: 1px solid #f0edff;
}

.bl-add-category-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.65rem;
  padding: 0.75rem 1.1rem;
  background: linear-gradient(180deg, #faf9ff 0%, #fff 100%);
  border-bottom: 1px solid #f0edff;
}

.bl-add-category-label {
  display: inline-flex;
  align-items: center;
  margin: 0;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #7c5dfa;
  white-space: nowrap;
}

.bl-add-category-select {
  flex: 1 1 220px;
  min-width: 200px;
  max-width: 360px;
}

.bl-edit-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.65rem;
  margin-top: 1.25rem;
  padding-top: 1.15rem;
  border-top: 1px solid #f0edff;
}

.bl-edit-btn-cancel,
.bl-edit-btn-save {
  min-width: 120px;
  height: 42px;
  border-radius: 10px !important;
  font-weight: 700;
  font-size: 0.88rem;
}

.bl-edit-btn-save {
  background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%) !important;
  border: none !important;
  box-shadow: 0 4px 14px rgba(115, 103, 240, 0.35);

  &:disabled {
    opacity: 0.55;
    box-shadow: none;
  }
}

.bl-view-footer {
  margin-top: 1rem;
}

.modern-form-group {
  ::v-deep label {
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #8a86b7;
  }
}

.book-list-sections-wrap {
  max-height: 420px;
  overflow-y: auto;
  padding: 0.85rem 1rem 1rem;
  background: #fff;
  scrollbar-width: thin;
  scrollbar-color: #d8d6de transparent;

  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d8d6de;
    border-radius: 999px;
  }
}

.bl-section-block {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);

  & + & {
    margin-top: 1rem;
  }
}

.bl-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(180deg, #f8f7ff 0%, #f3f0ff 100%);
  border-bottom: 2px solid #e0d8ff;
}

.bl-section-title {
  display: inline-flex;
  align-items: center;
  font-size: 0.95rem;
  font-weight: 800;
  color: #2d1b69;
  text-transform: uppercase;
  letter-spacing: 0.35px;
}

.bl-section-count-badge {
  min-width: 28px;
  font-weight: 700;
  font-size: 0.72rem;
}

.bl-section-table-wrap {
  overflow-x: auto;
}

.bl-section-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  thead th {
    background: #f8fafc;
    color: #475569;
    font-size: 0.72rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    padding: 0.7rem 0.85rem;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
  }

  tbody td {
    padding: 0.65rem 0.85rem;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover {
    background: #faf9ff;
  }

  th:first-child,
  td:first-child {
    width: 38%;
  }

  th:nth-child(2),
  td:nth-child(2) {
    width: 42%;
  }
}

.bl-section-table--view {
  .bl-section-item-name {
    font-weight: 700;
    color: #2d1b69;
  }

  .bl-section-item-particulars {
    color: #64748b;
  }
}

.bl-section-qty-col {
  width: 90px;
}

.bl-section-action-col {
  width: 48px;
}

.book-list-items-table-wrap {
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
  border-top: 1px solid #f0edff;
  background: #fff;
  scrollbar-width: thin;
  scrollbar-color: #d8d6de transparent;

  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #d8d6de;
    border-radius: 999px;

    &:hover {
      background: #c4c2cb;
    }
  }
}

.book-list-items-table {
  ::v-deep thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #f8f7ff !important;
    color: #2d1b69 !important;
    font-weight: 700;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    border-color: #e0d8ff !important;
    box-shadow: 0 1px 0 #f0edff;
  }

  ::v-deep tbody td {
    vertical-align: middle;
    border-color: #f0edff !important;
  }
}

.book-list-qty-input,
.bl-edit-qty-input {
  max-width: 90px;
  height: 38px;
  margin: 0 auto;
  text-align: center;
  font-weight: 700;
  color: #2d1b69;
  border: 2px solid #e0d8ff;
  border-radius: 8px;
  background: #faf9ff;

  &:focus {
    border-color: #7367f0;
    box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.12);
    background: #fff;
  }
}

.bl-item-text-input {
  min-width: 140px;
  height: 38px;
  border: 1.5px solid #e0d8ff;
  border-radius: 8px;
  background: #fff;
  font-size: 0.85rem;
  color: #2d1b69;

  &:focus {
    border-color: #7367f0;
    box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.1);
  }
}

.bl-item-category-badge {
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.35rem 0.65rem;
}

.book-list-item-actions-col {
  width: 48px;
}

.book-list-item-remove-btn {
  color: #dc2626;

  &:hover {
    color: #b91c1c;
  }
}

.bl-view-item-icon {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(124, 93, 250, 0.12);
  color: #7c5dfa;
  flex-shrink: 0;
}

.bl-qty-badge {
  min-width: 42px;
  font-weight: 700;
}

.bl-items-table {
  thead th {
    background: #f8f7ff !important;
    color: #2d1b69 !important;
    font-weight: 700;
    border-color: #e0d8ff !important;
  }

  tbody td {
    vertical-align: middle;
    border-color: #f0edff !important;
  }
}

.leave-details-section {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 16px;
  padding: 1.25rem;
  box-shadow: 0 4px 12px rgba(115, 103, 240, 0.08);
  position: relative;

  &--items,
  &.bl-class-section {
    overflow: visible;
  }

  &:not(.leave-details-section--items):not(.bl-class-section) {
    overflow: hidden;
  }

  .section-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1rem;
    padding-bottom: 0.85rem;
    border-bottom: 2px solid #f0edff;
  }

  .section-icon {
    color: #7c5dfa;
    stroke-width: 2.5;
    flex-shrink: 0;
  }

  .section-title {
    font-size: 0.95rem;
    font-weight: 800;
    color: #2d1b69;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

.details-meta-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.85rem;
}

.details-meta-item {
  background: linear-gradient(180deg, #ffffff 0%, #f8f7ff 100%);
  border-radius: 12px;
  padding: 0.85rem 1rem;
  border: 1.5px solid #f0edff;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.06);

  .details-label {
    display: block;
    font-size: 0.65rem;
    text-transform: uppercase;
    color: #8a86b7;
    letter-spacing: 0.8px;
    font-weight: 700;
    margin-bottom: 0.4rem;
  }

  .details-value {
    display: block;
    font-size: 0.95rem;
    font-weight: 700;
    color: #2d1b69;
    line-height: 1.3;

    &.details-value-primary {
      color: #7c5dfa;
    }

    &.details-value-info {
      color: #7367f0;
    }

    &.details-value-success {
      color: #10b981;
    }
  }
}

@media (max-width: 576px) {
  .bl-edit-body {
    padding: 1rem;
  }

  .bl-edit-modal-header {
    padding: 1rem;
  }
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

/* append-to-body mounts menu on document.body — needs global styles */
.bl-modal-select-dropdown.vs__dropdown-menu {
  z-index: 10600 !important;
  position: fixed !important;
  border-radius: 12px !important;
  border: 1.5px solid #e0d8ff !important;
  box-shadow: 0 8px 24px rgba(115, 103, 240, 0.18) !important;
  background: #fff !important;
  max-height: 280px !important;
  overflow-y: auto !important;
  margin-top: 0 !important;
  padding: 0.25rem 0 !important;
}

.bl-modal-select-dropdown .vs__dropdown-option {
  padding: 0.65rem 1rem !important;
  color: #2d1b69 !important;
  font-weight: 500 !important;

  &--highlight {
    background: #f3f0ff !important;
    color: #7c5dfa !important;
  }
}
</style>
